/**
 * Permission constants for the application
 * These should match exactly with the permission strings in roles-permissions.json
 */
// TODO-Imran-P2: We should use these consts in the code. Currently the strings are used directly in the code.s
export const Permissions = {
  // Client management
  CLIENT_INVITE: "Client - Invite for Account creation",
  
  // Staff management
  STAFF_ADD_REMOVE: "Staff - Add / Remove",
  STAFF_ASSIGN_ROLES: "Staff - Assign / Unassign roles",
  STAFF_ASSIGN_CLIENTS: "Staff - Assign / Unassign clients",
  
  // Loan functionality
  SIZER_ADJUSTMENTS: "Sizer + Adjustments",
  LOAN_TERMS: "Loan Terms",
  LOAN_TERMS_INVITE_CLIENT: "Loan Terms - Invite Client",
  LOAN_TERMS_CANCEL_APPLICATION: "Loan Terms - Cancel Application",
  LOAN_TERMS_SEND_PREQUAL_EMAIL: "Loan Terms - Send Pre-Qual Email",
  START_NEW_LOAN: "Start a New Loan",
  LOAN_DETAILS_DOWNLOAD: "Loan details - download",
  LOAN_STAGE_UPDATE: "Loan stage - Update",
  // Dedicated admin/manager-only permission for loan removal from Management -> Loans.
  LOAN_DELETE: "Actions - Delete Application",
  
  // Actions
  ACTIONS_UPDATE: "Actions - Update Status",
  ACTIONS_VIEW: "Actions - View",
  NEXT_STEPS_INTERACT: "Next Steps - Interact",
  
  // Third-party status
  THIRD_PARTY_FILE_UPLOAD: "3rd party status - File Upload",
  THIRD_PARTY_UPDATE: "3rd party status - Update Status",
  THIRD_PARTY_OVERRIDE_VIEW_DOWNLOAD: "3rd party status - Override View Download",
  THIRD_PARTY_VIEW: "3rd party status - View",
  
  // Application form
  APPLICATION_EDIT: "Application Form - Edit",
  APPLICATION_VIEW: "Application Form - View",
  APPLICATION_OVERRIDE_READONLY_FIELDS: "Application Form - Override readonly fields",
  
  // Annotations
  ANNOTATIONS_VIEW: "Annotations - View",
  ANNOTATIONS_CREATE: "Annotations - Create",
  ANNOTATIONS_UPDATE: "Annotations - Update",
  ANNOTATIONS_DELETE: "Annotations - Delete",
  ANNOTATIONS_ADD: "Annotations - Add",
  ANNOTATIONS_REPLY: "Annotations - Reply",
  ANNOTATIONS_RESOLVE: "Annotations - Resolve",
  ANNOTATIONS_ALLOW_UPDATES_OVERRIDE: "Annotations - Allow updates override",
  
  // Documents
  DOCUMENTS_ADD: "New fields / documents - Add",
  
  // Borrower/Broker specific
  PROVIDE_CONSENT: "Provide Consent to Loan Application",
  PAYMENTS_ACCESS: "Payments - Access",
  PAYMENTS_VERIFY: "Payments - Verify",
  ESIGN_ACCESS: "eSign - Access",

  // Guarantor
  GUARANTOR_PAYMENT_MANAGE: "Guarantor Payment - Manage",
  GUARANTOR_SELECTION_EDIT: "Guarantor Selection - Edit",
  GUARANTOR_SIGN_SEND: "Guarantor Sign - Send",
} as const;

// Type for permission values
//export type PermissionValue = typeof Permissions[keyof typeof Permissions];

// Role constants
export const Roles = {
  LOAN_OFFICER: "Loan Officer",
  PROCESSOR: "Processor",
  UNDERWRITER: "Underwriter",
  MANAGER: "Manager",
  ADMIN: "Admin",
  VAL_DEV: "Val Development",
  BORROWER: "Borrower",
  BROKER: "Broker",
  BORROWER_VIA_BROKER: "Borrower-via-Broker",
} as const;

export type RoleKey = keyof typeof Roles;

export const RoleGroups = {
  STAFF: [
    'LOAN_OFFICER',
    'PROCESSOR',
    'UNDERWRITER',
    'MANAGER',
    'ADMIN',
    'VAL_DEV'
  ],
  CLIENT: [
    'BORROWER',
    'BROKER',
    'BORROWER_VIA_BROKER'
  ],
  DIRECT_CLIENT: [
    'BORROWER',
    'BROKER'
  ]
};

const STAFF_ROLE_KEYS = new Set<string>(RoleGroups.STAFF);
const CLIENT_ROLE_KEYS = new Set<string>(RoleGroups.CLIENT);
const ROLE_KEYS = new Set<string>(Object.keys(Roles));
const DISPLAY_TO_KEY = new Map<string, string>(
  Object.entries(Roles).map(([key, display]) => [display, key])
);

// Helper function to determine if a role is a staff role
export function isStaff(role: string): boolean {
  return ['LOAN_OFFICER', 'PROCESSOR', 'UNDERWRITER', 'ADMIN', 'MANAGER'].includes(String(role || '').toUpperCase());
}

export function isClient(role: string): boolean {
  return ['BORROWER', 'BROKER', 'BORROWER_VIA_BROKER'].includes(String(role || '').toUpperCase());
}

export function isDirectClient(role: string): boolean {
  return ['BORROWER', 'BROKER'].includes(String(role || '').toUpperCase());
}

function toRoleKey(role?: string | null): string | null {
  if (!role) return null;
  if (ROLE_KEYS.has(role)) return role;
  const displayKey = DISPLAY_TO_KEY.get(role);
  if (displayKey) return displayKey;
  // Callers pass role keys in mixed case (e.g. 'loan_officer' from session data).
  const upperKey = role.toUpperCase();
  return ROLE_KEYS.has(upperKey) ? upperKey : null;
}

export function isStaffRole(role?: string | null, allowedStaffRoleKeys?: string[]): boolean {
  const roleKey = toRoleKey(role);
  if (!roleKey || !STAFF_ROLE_KEYS.has(roleKey)) return false;
  if (!allowedStaffRoleKeys || allowedStaffRoleKeys.length === 0) return true;
  return new Set(allowedStaffRoleKeys).has(roleKey);
}

export function isClientRole(role?: string | null, allowedClientRoleKeys?: string[]): boolean {
  const roleKey = toRoleKey(role);
  if (!roleKey || !CLIENT_ROLE_KEYS.has(roleKey)) return false;
  if (!allowedClientRoleKeys || allowedClientRoleKeys.length === 0) return true;
  return new Set(allowedClientRoleKeys).has(roleKey);
}

export function isBorrowerViaBroker(role?: string | null): boolean {
  return toRoleKey(role) === 'BORROWER_VIA_BROKER';
}

export function isBroker(role?: string | null): boolean {
  return toRoleKey(role) === 'BROKER';
}

export function hasAnyStaffRole(roles?: string[] | null, allowedStaffRoleKeys?: string[]): boolean {
  if (!roles || roles.length === 0) return false;
  return roles.some((role) => isStaffRole(role, allowedStaffRoleKeys));
}

export function hasAnyClientRole(roles?: string[] | null, allowedClientRoleKeys?: string[]): boolean {
  if (!roles || roles.length === 0) return false;
  return roles.some((role) => isClientRole(role, allowedClientRoleKeys));
}

// Type for role values
//export type RoleValue = typeof Roles[keyof typeof Roles];
